import * as React from "react";
import {
    Typography,
    Button,
    DialogTitle,
    Dialog,
    TextField,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import {
    apiBulkForceCloseWorkitems,
    apiBulkRollbackCloseWorkitems,
    BulkForceCloseResponse,
} from "../../../utils/api";
import { SnackbarContext } from "../../../context/AlertsContext";

type Mode = 'close' | 'rollback';

/**
 * Minimum row shape expected by MoveToClosedDialog.
 * cqaWorkItemId is optional so that rows still loading their CQA transform
 * (which adds this field asynchronously) are detected rather than silently dropped.
 */
export type CqaWorkItemRow = {
    cqaWorkItemId?: number;
    [key: string]: unknown;
};

type Props = {
    selected: CqaWorkItemRow[];
    open: boolean;
    handleClose: () => void;
    /**
     * Called after a successful submit with the ids that actually succeeded
     * (outcome CLOSED / ROLLED_BACK). SKIPPED ids are intentionally excluded so
     * the caller only optimistically removes rows that truly changed.
     */
    handleAfterClose: (succeededIds: number[]) => void;
    mode: Mode;
    /**
     * Header "select all" under the server-side row model gives only the loaded rows,
     * so bulk actions are blocked in this state rather than silently affecting a subset.
     */
    allSelected?: boolean;
};

const REASON_OPTIONS: { value: string; label: string }[] = [
    { value: "INVALID", label: "Invalid" },
    { value: "ABANDONED", label: "Abandoned" },
    { value: "DUPLICATE", label: "Duplicate" },
    { value: "NO_LONGER_NEEDED", label: "No longer needed" },
];

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': { padding: theme.spacing(2) },
}));

const StyledButton = styled(Button)({
    borderRadius: "4px",
    textTransform: "none",
    flexGrow: 1,
    maxWidth: '160px',
    maxHeight: '40px',
    minWidth: '120px',
    minHeight: '40px',
});

/**
 * Selected rows are flattened AG-Grid row data (ColumnDataCall lifts workitem.data to the row),
 * so the CQA WorkItem id is a top-level field: row.cqaWorkItemId — NOT row.data.cqaWorkItemId.
 */
const distinctWorkItemIds = (selected: CqaWorkItemRow[]): number[] => {
    const ids = (selected ?? [])
        .map((row) => row?.cqaWorkItemId)
        .filter((id): id is number => typeof id === "number");
    return Array.from(new Set(ids));
};

// Human-readable labels for the backend outcome enum shown in the result snackbar.
const OUTCOME_LABELS: Record<string, string> = {
    CLOSED: "Closed",
    ROLLED_BACK: "Rolled Back",
    SKIPPED: "Skipped",
};

const humanizeOutcome = (outcome: string): string =>
    OUTCOME_LABELS[outcome] ??
    outcome
        .toLowerCase()
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());

const summarize = (results: BulkForceCloseResponse["results"]): string => {
    const counts = results.reduce<Record<string, number>>((acc, r) => {
        acc[r.outcome] = (acc[r.outcome] ?? 0) + 1;
        return acc;
    }, {});
    return Object.entries(counts)
        .map(([outcome, n]) => `${n} ${humanizeOutcome(outcome)}`)
        .join(", ");
};

export const MoveToClosedDialog = ({
    selected,
    open,
    handleClose,
    handleAfterClose,
    mode,
    allSelected,
}: Props) => {
    const { openSnackbar } = React.useContext(SnackbarContext);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [reasonCategory, setReasonCategory] = React.useState<string>("");
    const [note, setNote] = React.useState("");

    const ids = React.useMemo(() => distinctWorkItemIds(selected), [selected]);
    // A row is "ready" once the async CQA transform has attached its numeric cqaWorkItemId;
    // rows without one are still loading or aren't gradeable CQA cases, so they are skipped.
    const selectedCount = (selected ?? []).length;
    const readyCount = React.useMemo(
        () => (selected ?? []).filter((row) => typeof row?.cqaWorkItemId === "number").length,
        [selected],
    );
    const skippedCount = selectedCount - readyCount;
    const noneReady = ids.length === 0;
    const isClose = mode === "close";
    const title = isClose ? "Move to Closed" : "Rollback Close";
    const confirmLabel = isClose ? "Move to Closed" : "Rollback";

    const reset = () => {
        setReasonCategory("");
        setNote("");
    };

    const handleDialogClose = () => {
        reset();
        handleClose();
    };

    const handleConfirm = async () => {
        if (ids.length === 0 || !reasonCategory || allSelected) return;
        setIsSubmitting(true);
        try {
            const trimmedNote = note.trim() === "" ? undefined : note.trim();
            const response = isClose
                ? await apiBulkForceCloseWorkitems(ids, reasonCategory, trimmedNote)
                : await apiBulkRollbackCloseWorkitems(ids, reasonCategory, trimmedNote);
            const succeededIds = response.results
                .filter((r) => r.outcome === "CLOSED" || r.outcome === "ROLLED_BACK")
                .map((r) => r.workItemId);
            // Anything not closed/rolled-back — skipped OR a backend error/failure — is a partial
            // result, so warn rather than report success for those too.
            const notSucceeded = response.results.length - succeededIds.length;
            openSnackbar(
                `${summarize(response.results)}${notSucceeded ? " — some items were not updated" : ""}`,
                notSucceeded ? "warning" : "success"
            );
            handleAfterClose(succeededIds);
            handleDialogClose();
        } catch (err) {
            console.error(`Error moving work items to ${isClose ? "closed" : "rolled back"}:`, err);
            openSnackbar(
                `Failed to ${isClose ? "close" : "roll back"} work items. Please try again.`,
                "error"
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const confirmDisabled = noneReady || !reasonCategory || isSubmitting || !!allSelected;

    return (
        <StyledDialog open={open} onClose={handleDialogClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ m: 0 }} id="move-to-closed-title">
                {title}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={handleDialogClose}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (t) => t.palette.grey[500],
                }}
                disabled={isSubmitting}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent>
                {allSelected ? (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        &quot;Select all&quot; affects only the rows currently loaded in the grid. Select the
                        specific work items you want to {isClose ? "close" : "roll back"} instead.
                    </Alert>
                ) : noneReady ? (
                    <Alert severity="warning" sx={{ mb: 2 }} data-testid="stale-rows-warning">
                        None of the selected work items are ready — they are still loading, or are not
                        gradeable CQA cases. Close this dialog, wait a moment, and reselect.
                    </Alert>
                ) : skippedCount > 0 ? (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {ids.length} of {selectedCount} work items will be{" "}
                        {isClose ? "moved to Closed" : "rolled back"}. {skippedCount}{" "}
                        {skippedCount === 1 ? "is" : "are"} not ready and will be skipped.
                    </Alert>
                ) : (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {ids.length} work item{ids.length === 1 ? "" : "s"} selected
                    </Alert>
                )}
                <FormControl fullWidth sx={{ mb: 2 }} required>
                    <InputLabel id="close-reason-label">Reason</InputLabel>
                    <Select
                        labelId="close-reason-label"
                        label="Reason"
                        value={reasonCategory}
                        onChange={(e) => setReasonCategory(e.target.value)}
                        disabled={isSubmitting}
                    >
                        {REASON_OPTIONS.map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                                {o.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    label="Note (optional)"
                    multiline
                    rows={3}
                    fullWidth
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    sx={{ mb: 2 }}
                    disabled={isSubmitting}
                />
                <Typography>Are you sure you want to proceed?</Typography>
            </DialogContent>
            <DialogActions>
                <StyledButton
                    variant="outlined"
                    onClick={handleDialogClose}
                    disabled={isSubmitting}
                >
                    Cancel
                </StyledButton>
                <StyledButton
                    variant="contained"
                    sx={{ ml: 5 }}
                    disabled={confirmDisabled}
                    onClick={handleConfirm}
                >
                    {isSubmitting ? "Working..." : confirmLabel}
                </StyledButton>
            </DialogActions>
        </StyledDialog>
    );
};
